import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {MeasurementSystemType} from 'types/Localization'
import {
  CARD_IMAGE_HEIGHT,
  CARD_WIDTH,
  PriceDetailsSkeleton
} from 'widgets/components/HotelCarousel/HotelCard/SuggestionHotelCardSkeleton'
import {convertKmToMiles} from 'widgets/HotelCardWidget/HotelInfo/utils'

import {Button} from '@daedalus/atlas/Button'
import {Divider} from '@daedalus/atlas/Divider'
import {Grid} from '@daedalus/atlas/helpers/Grid'
import {Icon} from '@daedalus/atlas/Icon'
import {Image} from '@daedalus/atlas/Image'
import placeHolderImage from '@daedalus/atlas/images/placeholder-hotel-image.svg'
import {PriceComposition} from '@daedalus/atlas/PriceComposition'
import {StarRating} from '@daedalus/atlas/StarRating'
import {Text} from '@daedalus/atlas/Text'
import {useBrand} from '@daedalus/core/src/brand/hooks/useBrand'
import {getBrandPrivateDealMessage} from '@daedalus/core/src/brand/modules/selectors'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {hasAnchorPrice} from '@daedalus/core/src/offer/business/price'
import {ImageProvider} from '@daedalus/core/src/staticContent/types/ImageProvider'
import {
  getRoundingStrategy,
  RoundingStrategy
} from '@daedalus/core/src/utils/number'
import {PrivateDealTag} from '@findhotel/shared/src/privateDeal/PrivateDealTag'
import {ReviewRating} from '@findhotel/shared/src/search/ReviewRating'

const ClickArea = styled.div(
  ({theme}) => css`
    background-color: ${theme.colors.background.neutral.c000};
    border: 1px solid ${theme.colors.border.neutral.c100};
    display: flex;
    flex-direction: column;
    border-radius: ${theme.layout.radius.lg}px;
    cursor: pointer;
  `
)

const TopSection = styled.section(({theme}) => {
  return css`
    position: relative;
    display: flex;
    flex-direction: column;
    width: ${CARD_WIDTH}px;
    border-radius: ${theme.layout.radius.lg}px ${theme.layout.radius.lg}px 0 0;
    overflow: hidden;
    z-index: 1;
  `
})

const GalleryAndDetailsComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const BottomSection = styled.section(
  ({theme}) => css`
    width: ${CARD_WIDTH}px;
    border-radius: ${theme.layout.radius.lg}px;
    overflow: hidden;
    margin-top: ${theme.layout.spacing.s400}px;
  `
)

const GalleryComponentWrapper = styled.div`
  position: relative;
  height: ${CARD_IMAGE_HEIGHT}px;
  width: 100%;
`

const PropertyDetailsComponentWrapper = styled.div(
  ({theme}) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 0 ${theme.layout.spacing.s300}px;
  `
)

const PropertyNameRatingAndDistance = styled.div(
  ({theme}) => css`
    margin-bottom: ${theme.layout.spacing.s400}px;
    min-height: 94px;
  `
)

const NameAndRatingWrapper = styled.div<{withIncreasedMargin: boolean}>(
  ({theme, withIncreasedMargin}) => css`
    margin-bottom: ${withIncreasedMargin
      ? theme.layout.spacing.s200
      : theme.layout.spacing.s100}px;
  `
)

const HotelName = styled(Text)(
  ({theme}) => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: ${theme.layout.spacing.s100}px;
  `
)

const Wrapper = styled('div')<{hasInsideMargin: boolean}>(
  ({theme, hasInsideMargin}) => css`
    display: flex;
    width: ${CARD_WIDTH}px;
    position: relative;
    ${hasInsideMargin &&
    `&:last-of-type {
      margin-right: ${theme.layout.spacing.s500}px;
      }
      &:first-of-type {
        margin-left: ${theme.layout.spacing.s500}px;
      }
    `}
  `
)

const PriceSectionWrapper = styled.div(
  ({theme}) => css`
    display: flex;
    flex-direction: column;
    margin-top: ${theme.layout.spacing.s400}px;
  `
)

const SavingTagWrapper = styled.div(
  ({theme}) => css`
    position: absolute;
    z-index: 1;
    top: ${theme.layout.spacing.s300}px;
    left: ${theme.layout.spacing.s300}px;
  `
)

const StyledPriceComposition = styled(PriceComposition)(
  () => css`
    justify-content: flex-start;
  `
)

export const DistanceWrapper = styled(Grid)(
  ({theme}) => css`
    color: ${theme.colors.content.neutral.c600};
    margin-bottom: ${theme.layout.spacing.s200}px;
  `
)

export const AddressWrapper = styled(Grid)(
  ({theme}) => css`
    color: ${theme.colors.content.neutral.c600};
    margin-bottom: ${theme.layout.spacing.s100}px;
  `
)

export const AddressText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const ButtonWrapper = styled('div')(
  ({theme}) => css`
    padding: 0 ${theme.layout.spacing.s300}px;
    margin: ${theme.layout.spacing.s400}px 0;
  `
)

const StyledIcon = styled(Icon)`
  & svg {
    /* @mirror */
  }
`

interface PriceDetailsProps {
  anchorPrice: number
  price: number
  currency: string
  roundingStrategy: RoundingStrategy
}

export const messages = defineMessages({
  distanceAsMiles: {
    id: 'distanceAsMiles',
    defaultMessage: 'miles'
  },
  distanceAsKm: {
    id: 'distanceAsKm',
    defaultMessage: 'km'
  },
  distanceFromCityCenter: {
    id: 'distanceFromCityCenter',
    defaultMessage: '{distance} {unit} to city center'
  }
})

const PriceDetail = ({
  anchorPrice,
  price,
  currency,
  roundingStrategy
}: PriceDetailsProps) => {
  const showAnchorPrice = hasAnchorPrice(price, anchorPrice)

  if (!price) {
    return <PriceDetailsSkeleton />
  }

  return (
    <StyledPriceComposition
      size={'xl'}
      showLockIcon={false}
      showRowLayout
      showAnchorPrice={showAnchorPrice}
      anchorPrice={anchorPrice}
      currencyCode={currency}
      price={price}
      roundingStrategy={roundingStrategy}
    />
  )
}

interface Props {
  hotelName: string
  starRating?: number
  reviewRating?: number
  reviewRatingCount?: number
  imageUrl?: string
  price: number
  anchorPrice?: number
  currency: string
  roundingStrategy?: RoundingStrategy
  imageProvider: ImageProvider
  onHotelClick?: (event: React.MouseEvent) => void
  offerDDActionName?: string
  savingPercentage?: number
  hasInsideMargin?: boolean
  distanceFromCityCentre?: number
  distanceUnit: MeasurementSystemType
  address: string
}

export const SuggestionHotelCard = ({
  hotelName,
  starRating,
  reviewRating,
  reviewRatingCount,
  imageUrl,
  currency,
  price,
  imageProvider,
  anchorPrice = 0,
  roundingStrategy = getRoundingStrategy(),
  onHotelClick,
  offerDDActionName,
  savingPercentage,
  hasInsideMargin = false,
  distanceFromCityCentre,
  distanceUnit = 'metric'
}: Props) => {
  const {formatMessage} = useIntl()
  const {brandCode} = useBrand()
  const brandPrivateDealMessage = useSelector(getBrandPrivateDealMessage)

  let distanceMessage: null | string = null

  if (distanceFromCityCentre) {
    const distanceInKm = distanceFromCityCentre / 1000
    const [unitMessage, convertedDistance] =
      distanceUnit === 'metric'
        ? [messages.distanceAsKm, distanceInKm]
        : [messages.distanceAsMiles, convertKmToMiles(distanceInKm)]

    const formattedDistance = convertedDistance.toFixed(1)

    // eslint-disable-next-line fp/no-mutation
    distanceMessage =
      Number(formattedDistance) > 0
        ? formatMessage(messages.distanceFromCityCenter, {
            distance: formattedDistance,
            unit: formatMessage(unitMessage)
          })
        : null
  }

  return (
    <Wrapper
      data-dd-action-name={offerDDActionName}
      hasInsideMargin={hasInsideMargin}
    >
      <ClickArea role="button" tabIndex={0} onClick={onHotelClick}>
        <TopSection>
          {savingPercentage && (
            <SavingTagWrapper>
              <PrivateDealTag
                savingPercentage={savingPercentage}
                message={brandPrivateDealMessage}
                brandCode={brandCode}
              />
            </SavingTagWrapper>
          )}

          <GalleryAndDetailsComponentWrapper>
            <GalleryComponentWrapper data-id="hotelCardImage">
              {imageUrl && (
                <Image
                  imageProvider={imageProvider}
                  url={imageUrl}
                  isResponsive
                  size="large"
                  placeHolderImage={placeHolderImage}
                />
              )}
            </GalleryComponentWrapper>
          </GalleryAndDetailsComponentWrapper>
        </TopSection>
        <BottomSection>
          <PropertyDetailsComponentWrapper data-id="propertyDetails">
            <PropertyNameRatingAndDistance>
              <NameAndRatingWrapper withIncreasedMargin={!distanceMessage}>
                {starRating && <StarRating rating={starRating} size="xs" />}
                <HotelName variant="labelM">{hotelName}</HotelName>
              </NameAndRatingWrapper>

              {/* Leaving here for now in case we decide to switch back
               <AddressWrapper
                container
                alignItems="center"
                justify="flex-start"
                spacing="s000"
                wrap="nowrap"
              >
                <StyledIcon name="Pin" size="xs" />
                <AddressText variant="bodyXS">{address}</AddressText>
              </AddressWrapper> */}

              {distanceMessage && (
                <DistanceWrapper
                  container
                  alignItems="center"
                  justify="flex-start"
                  spacing="s000"
                  wrap="nowrap"
                >
                  <StyledIcon name="Walk" size="xs" />
                  <Text variant="bodyXS">{distanceMessage}</Text>
                </DistanceWrapper>
              )}

              {reviewRating && (
                <ReviewRating
                  rating={reviewRating}
                  totalRatingCount={reviewRatingCount}
                />
              )}
            </PropertyNameRatingAndDistance>

            <Divider />

            <PriceSectionWrapper data-id="priceDetails">
              <Text variant="bodyXS" colorPath="content.neutral.c600">
                <FormattedMessageWrapper
                  id="multipleHotelsWidgetWithSearch.priceLabel"
                  defaultMessage="Best nightly price"
                />
              </Text>

              <PriceDetail
                price={price}
                anchorPrice={anchorPrice}
                currency={currency}
                roundingStrategy={roundingStrategy}
              />
            </PriceSectionWrapper>
          </PropertyDetailsComponentWrapper>
        </BottomSection>
        <ButtonWrapper>
          <Button
            iconEnd={<StyledIcon size="sm" name="Share" />}
            size="md"
            fullWidth
            dataId="viewDealButton"
          >
            <FormattedMessageWrapper id="viewDeal" defaultMessage="View deal" />
          </Button>
        </ButtonWrapper>
      </ClickArea>
    </Wrapper>
  )
}
